import { useState } from "react";
import { AdminSidebar } from "@/components/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

const COMING_SOON_FEATURES = [
  {
    id: 1,
    title: "AI-Powered Safety Coaching",
    description: "Real-time AI coaching that identifies unsafe behaviors in training videos and provides personalized feedback.",
    icon: "M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z",
    category: "AI & Analytics",
    eta: "Q2 2025"
  },
  {
    id: 2,
    title: "Multi-Language Voice Over",
    description: "Automatic translation and voice-over generation for training videos in 12+ regional languages.",
    icon: "M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129",
    category: "Localization",
    eta: "Q1 2025"
  },
  {
    id: 3,
    title: "Incident Reporting Module",
    description: "Workers can report near-misses and safety incidents directly from the app with photo evidence.",
    icon: "M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z",
    category: "Safety & Compliance",
    eta: "Q1 2025"
  },
  {
    id: 4,
    title: "Skill Matrix & Competency Tracking",
    description: "Track worker competencies, skill gaps, and certification requirements across your organization.",
    icon: "M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01",
    category: "HR & Development",
    eta: "Q2 2025"
  },
  {
    id: 5,
    title: "Shift-Based Training Scheduler",
    description: "Automatically schedule training sessions based on shift patterns and production schedules.",
    icon: "M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z",
    category: "Operations",
    eta: "Q1 2025"
  },
  {
    id: 6,
    title: "AR Safety Simulations",
    description: "Augmented reality modules for hands-on safety training without real-world risks.",
    icon: "M15 12a3 3 0 11-6 0 3 3 0 016 0z M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z",
    category: "Training Innovation",
    eta: "Q3 2025"
  },
  {
    id: 7,
    title: "Contractor & Visitor Training",
    description: "Quick onboarding modules for contractors and visitors with QR code-based access.",
    icon: "M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z",
    category: "Access Management",
    eta: "Q1 2025"
  },
  {
    id: 8,
    title: "Equipment-Specific Training Paths",
    description: "Dynamic training paths that adapt based on equipment certifications and job requirements.",
    icon: "M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z",
    category: "Training Automation",
    eta: "Q2 2025"
  },
  {
    id: 9,
    title: "Real-Time Compliance Dashboard",
    description: "Live compliance status across all facilities with automated regulatory reporting.",
    icon: "M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z",
    category: "Compliance",
    eta: "Q1 2025"
  },
  {
    id: 10,
    title: "Integration Hub",
    description: "Connect with SAP, Oracle, Workday, and other enterprise systems for seamless data sync.",
    icon: "M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4",
    category: "Enterprise",
    eta: "Q2 2025"
  }
];

export default function OperationsSuite() {
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [selectedFeature, setSelectedFeature] = useState(null);
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  const handleRequestAccess = (feature) => {
    setSelectedFeature(feature);
    setShowRequestModal(true);
  };

  const handleSubmitRequest = async () => {
    if (!email) {
      toast.error("Please enter your email");
      return;
    }
    
    setSubmitting(true);
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    toast.success(`Early access request submitted for ${selectedFeature?.title}!`);
    setShowRequestModal(false);
    setEmail("");
    setCompany("");
    setSubmitting(false);
  };

  return (
    <div className="flex">
      <AdminSidebar active="operations" />
      <div className="admin-content">
        {/* Hero Section */}
        <div className="bg-gradient-to-br from-[#0a2240] to-[#2e7fd4] rounded-2xl p-8 mb-8 text-white">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
              <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </div>
            <span className="px-3 py-1 bg-white/20 rounded-full text-sm font-semibold">Coming Soon</span>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold mb-3" data-testid="operations-suite-title">Operations Suite</h1>
          <p className="text-white/80 text-lg max-w-2xl">
            Powerful tools to transform your industrial training and safety operations. 
            Request early access to shape the features that matter most to you.
          </p>
        </div>

        {/* Feature Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          {COMING_SOON_FEATURES.map((feature) => (
            <div
              key={feature.id}
              className="bg-white border border-slate-200 rounded-xl p-6 hover:shadow-lg hover:border-[#2e7fd4]/30 transition-all group"
              data-testid={`feature-card-${feature.id}`}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-[#2e7fd4]/10 to-[#0e9f8e]/10 rounded-xl flex items-center justify-center group-hover:from-[#2e7fd4]/20 group-hover:to-[#0e9f8e]/20 transition-colors">
                  <svg className="w-6 h-6 text-[#2e7fd4]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={feature.icon} />
                  </svg>
                </div>
                <span className="text-xs font-semibold text-slate-500 bg-slate-100 px-2 py-1 rounded">
                  {feature.eta}
                </span>
              </div>
              
              <h3 className="text-lg font-bold text-slate-900 mb-2">{feature.title}</h3>
              <p className="text-sm text-slate-600 mb-4 line-clamp-3">{feature.description}</p>
              
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-[#2e7fd4] bg-[#2e7fd4]/10 px-2 py-1 rounded">
                  {feature.category}
                </span>
                <Button
                  onClick={() => handleRequestAccess(feature)}
                  variant="ghost"
                  size="sm"
                  className="text-[#2e7fd4] hover:bg-[#2e7fd4]/10 font-semibold"
                  data-testid={`request-access-${feature.id}`}
                >
                  Request Access
                </Button>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="bg-slate-50 border border-slate-200 rounded-xl p-8 text-center">
          <h3 className="text-xl font-bold text-slate-900 mb-2">Have a Feature Request?</h3>
          <p className="text-slate-600 mb-4 max-w-lg mx-auto">
            We're constantly building new capabilities based on customer feedback. 
            Let us know what features would help your operations.
          </p>
          <Button
            onClick={() => {
              setSelectedFeature({ title: "Custom Feature Request" });
              setShowRequestModal(true);
            }}
            className="bg-[#0a2240] hover:bg-[#0f325c] text-white"
            data-testid="custom-feature-request"
          >
            Submit Feature Request
          </Button>
        </div>

        {/* Request Modal */}
        {showRequestModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl p-8 max-w-md w-full" data-testid="request-modal">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-gradient-to-br from-[#2e7fd4] to-[#0e9f8e] rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                  </svg>
                </div>
                <h2 className="text-2xl font-bold text-slate-900 mb-2">Request Early Access</h2>
                <p className="text-slate-600">
                  {selectedFeature?.title}
                </p>
              </div>

              <div className="space-y-4">
                <div>
                  <Label htmlFor="email" className="font-semibold">Work Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@company.com"
                    className="mt-1"
                    data-testid="request-email"
                  />
                </div>
                <div>
                  <Label htmlFor="company" className="font-semibold">Company Name</Label>
                  <Input
                    id="company"
                    value={company}
                    onChange={(e) => setCompany(e.target.value)}
                    placeholder="Your Company"
                    className="mt-1"
                    data-testid="request-company"
                  />
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <Button
                  onClick={() => setShowRequestModal(false)}
                  variant="outline"
                  className="flex-1"
                  disabled={submitting}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSubmitRequest}
                  className="flex-1 bg-gradient-to-r from-[#0a2240] to-[#2e7fd4] hover:from-[#0f325c] hover:to-[#1c64b0] text-white"
                  disabled={submitting}
                  data-testid="submit-request"
                >
                  {submitting ? "Submitting..." : "Request Access"}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
